import React from 'react';
import { Activity, User, CheckSquare, Target, Pencil, Building2, Link2, Sparkles } from 'lucide-react';
import InlineField from '../InlineField';

// One proposed change, as the AI suggested it: what kind of record, what it
// says, what would change, and why. Accept or reject with the checkbox; edit
// the words in place when the proposal allows it. Nothing here writes.

const KINDS = {
  activity: { icon: Activity, label: 'Activity' },
  contact: { icon: User, label: 'Contact' },
  task: { icon: CheckSquare, label: 'Action item' },
  goal: { icon: Target, label: 'Goal' },
  field: { icon: Pencil, label: 'Field' },
  account: { icon: Building2, label: 'Account' },
  opportunity: { icon: Target, label: 'Opportunity' },
  link: { icon: Link2, label: 'Link' },
};

export default function ProposalCard({ item, onToggle, onEdit, onFieldEdit, disabled = false }) {
  const kind = KINDS[item.kind] || { icon: Sparkles, label: item.kind || 'Change' };
  const Icon = kind.icon;
  const accepted = item.accepted !== false;
  const editable = !!onEdit && item.editable;
  // Fields the person can fix in place before applying (the assistant's
  // cards). A row the fields already show is not shown twice.
  const fields = onFieldEdit && Array.isArray(item.fields) ? item.fields : null;
  // A field that is the card's title (an action item's words) is edited in
  // the title itself rather than repeated as a row.
  const titleField = fields ? fields.find((f) => f.asTitle) || null : null;
  const rows = fields ? fields.filter((f) => !f.asTitle) : null;
  const shown = fields ? new Set(fields.map((f) => f.label)) : null;
  const titleEditable = editable || !!titleField;
  const changes = (item.changes || []).filter((c) => !shown || !shown.has(c.label));

  return (
    <div className={`ai-card ${accepted ? 'is-accepted' : 'is-rejected'}`} data-kind={item.kind}>
      <label className="ai-card-toggle checkbox-wrapper">
        <input
          type="checkbox"
          className="checkbox"
          checked={accepted}
          disabled={disabled}
          onChange={(e) => onToggle && onToggle(item.id, e.target.checked)}
          aria-label={titleEditable ? `Accept: ${item.title}` : undefined}
        />
        <span className="ai-card-kind"><Icon size={13} aria-hidden="true" /> {kind.label}</span>
        {!titleEditable && <span className="ai-card-title">{item.title}</span>}
      </label>
      {titleEditable && (
        <div className="ai-card-title ai-card-title-editable">
          <InlineField
            value={item.title}
            onSave={titleField ? (next) => onFieldEdit(item.id, titleField.key, next) : (title) => onEdit(item.id, { title })}
            label={`${kind.label} text`}
            canEdit={!disabled && (!titleField || accepted)}
            multiline={item.multiline}
          />
        </div>
      )}
      {item.description && <p className="ai-card-description">{item.description}</p>}
      {rows && rows.length > 0 && (
        <dl className="ai-card-changes ai-card-fields">
          {rows.map((f) => (
            <div key={f.key} className="ai-card-change ai-card-field">
              <dt>{f.label}</dt>
              <dd>
                <InlineField
                  value={f.value}
                  onSave={(next) => onFieldEdit(item.id, f.key, next)}
                  label={f.label}
                  type={f.type === 'date' ? 'date' : 'text'}
                  multiline={!!f.multiline}
                  emptyText={f.emptyText || 'Add'}
                  canEdit={!disabled && accepted}
                />
              </dd>
            </div>
          ))}
        </dl>
      )}
      {changes.length > 0 && (
        <dl className="ai-card-changes">
          {changes.map((c, i) => (
            <div key={i} className="ai-card-change">
              <dt>{c.label}</dt>
              <dd>
                {c.from !== undefined && c.from !== null && c.from !== '' && <><s>{String(c.from)}</s> <span aria-hidden="true">→</span> </>}
                <strong>{c.to === undefined || c.to === null || c.to === '' ? '—' : String(c.to)}</strong>
              </dd>
            </div>
          ))}
        </dl>
      )}
      {item.edited && <p className="ai-card-edited">Edited by you</p>}
      {item.reason && <p className="ai-card-reason">“{item.reason}”</p>}
      {item.extra && <div className="ai-card-extra">{item.extra}</div>}
    </div>
  );
}
